/**
 * Minting a survey on the device.
 *
 * The DEVICE mints the id, not the server. A field-initiated survey is created
 * in a basement with no signal (spec §2.1), so there is nobody to ask; and
 * because the id is stable, the eventual `POST /api/surveys` is idempotent on
 * it — a retried create after a dropped response lands on the same row instead
 * of producing a second survey.
 *
 * Ids are constrained to the API's `SAFE_ID` grammar (`[A-Za-z0-9][A-Za-z0-9._-]*`)
 * because a survey id ends up inside an R2 object key and a URL path.
 *
 * `emptySurveyDocument` used to live here and now lives in src/core/document.ts:
 * the assignment inbox (src/db/sync.ts) mints documents too, and the db layer
 * must not reach up into the UI to do it. It is re-exported so every existing
 * caller — and every test — keeps its import path.
 */

export { emptySurveyDocument } from '../../core/document';
export type { NewSurveyInput } from '../../core/document';

/** Injected so a test can produce byte-identical documents. */
export type IdSource = () => string;

export const randomId: IdSource = () => {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;
  // Older WebViews and any insecure origin lack randomUUID. 16 random bytes of
  // hex is the same entropy and the same grammar.
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
};

export function newSurveyId(newId: IdSource = randomId): string {
  return `srv-${newId()}`;
}
